import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useOutletContext, useParams } from "react-router-dom";
import { RiFileExcel2Line } from "react-icons/ri";
import { FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";
import { formatCurrency } from "../../../../helpers/functions.tsx";
import {
    formatAdminFiguresDateLabel,
    formatAdminFiguresDateTime,
    formatAdminFiguresTime,
} from "../../../../helpers/adminFiguresTime.tsx";
import {
    betsForLineType,
    closingLineCombinedLabel,
    closingLineDeltaLabel,
    closingLinePointsLabel,
    closingLinePriceLabel,
    finalBetLabel,
    lineTypeForKind,
    originalBetLabel,
    signedClassName,
    toClosingLineBetDate,
} from "../../../../helpers/closingLineAnalysis.ts";
import {
    CLOSING_LINE_CACHE_STALE_MS,
    CLOSING_LINE_CACHE_TTL_MS,
    getPlayerClosingLineAnalysis,
} from "../../../../services/admin/BetsService.tsx";
import { useCachedQuery } from "../../../../helpers/queryCache.tsx";
import { ClosingLineLineType } from "../../../../models/ClosingLineAnalysis.tsx";
import { VisitedUserOutletContext } from "../../../../pages/admin/LayoutUser.tsx";
import { useUser } from "../../../../context/UserContext.tsx";
import "../../../../styles/admin/ClosingLineAnalysis.css";

const formatWinLoss = (value: number) => `$${formatCurrency(value, 0)}`;
const SKELETON_ROWS = 6;

const ClosingLineTableSkeleton = () => (
    <>
        {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <tr key={index} aria-hidden="true">
                {Array.from({ length: 7 }).map((__, cell) => (
                    <td key={cell}>
                        <span className="rect-placeholder closing-line-skeleton-cell" />
                    </td>
                ))}
            </tr>
        ))}
    </>
);

const ClosingLineDetail = () => {
    const { user: currentUser, isLoading: isUserLoading } = useUser();
    const { user: routeUser } = useParams<{ user: string }>();
    const { visitedUser } = useOutletContext<VisitedUserOutletContext>();
    const userName = routeUser || visitedUser?.userName || "";
    const [lineType, setLineType] = useState<ClosingLineLineType>("price");
    const inferredRef = useRef(false);

    const { data: doc, isLoading } = useCachedQuery(
        userName ? `closingLineAnalysis:player:${userName}` : "",
        () => getPlayerClosingLineAnalysis(userName),
        [userName],
        { ttlMs: CLOSING_LINE_CACHE_TTL_MS, staleMs: CLOSING_LINE_CACHE_STALE_MS },
    );

    const rows = useMemo(
        () => betsForLineType(doc?.bets, lineType),
        [doc?.bets, lineType],
    );

    useEffect(() => {
        inferredRef.current = false;
        setLineType("price");
    }, [userName]);

    useEffect(() => {
        if (inferredRef.current || !doc) return;
        const bets = doc.bets ?? [];
        const hasPrice = bets.some((bet) => (bet.lineType || lineTypeForKind(bet.kind)) === "price");
        const hasPoints = bets.some((bet) => (bet.lineType || lineTypeForKind(bet.kind)) === "points")
            || doc.points != null
            || Boolean(doc.pointsDisplay?.trim());
        if (!hasPrice && hasPoints) setLineType("points");
        inferredRef.current = true;
    }, [doc]);
    const showSkeleton = isLoading && rows.length === 0;
    const averageLabel = lineType === "price"
        ? closingLinePriceLabel({ price: doc?.price ?? null, priceDisplay: doc?.priceDisplay ?? "" })
        : closingLinePointsLabel({ points: doc?.points ?? null, pointsDisplay: doc?.pointsDisplay ?? "" });
    const averageValue = lineType === "price" ? doc?.price : doc?.points;
    const winLoss = (doc?.bets?.length ?? 0) > 0
        ? rows.reduce((sum, row) => sum + (row.winLoss ?? 0), 0)
        : (doc?.winLoss ?? 0);

    const exportToExcel = () => {
        const sheetRows = rows.map((row) => ({
            Ticket: row.ticket || "",
            Accepted: toClosingLineBetDate(row) ? formatAdminFiguresDateTime(toClosingLineBetDate(row)!) : "",
            Description: row.description,
            "Original Bet": originalBetLabel(row),
            "Final Bet": finalBetLabel(row),
            "Closing Line": closingLineDeltaLabel(row.value, row.kind, row.valueDisplay) || closingLineCombinedLabel(row),
            Result: formatWinLoss(row.winLoss),
        }));
        const worksheet = XLSX.utils.json_to_sheet(sheetRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, userName || "Player");
        const today = new Date();
        const formattedDate = formatAdminFiguresDateLabel(today, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        const formattedTime = formatAdminFiguresTime(today, {
            hour: "numeric",
            minute: "2-digit",
        }).replace(":", "_").replace(" ", "");
        XLSX.writeFile(workbook, `ClosingLine_${userName}_${lineType}_${formattedDate.replace(",", "")} ${formattedTime}.xlsx`);
    };

    if (!isUserLoading && currentUser?.userType !== "masterAgent") {
        return <Navigate to="basics" replace />;
    }

    return (
        <div className="closing-line-page">
            <div className="mt-2 container-body">
                <div className="closing-line-header">
                    <div className="closing-line-filters">
                        <label className="closing-line-filter">
                            <span>Time</span>
                            <select className="closing-line-select" value="14" disabled>
                                <option value="14">Last 14 Days</option>
                            </select>
                        </label>
                        <label className="closing-line-filter">
                            <span>Line Type</span>
                            <select
                                className="closing-line-select"
                                value={lineType}
                                onChange={(event) => setLineType(event.target.value as ClosingLineLineType)}
                            >
                                <option value="price">Price</option>
                                <option value="points">Points</option>
                            </select>
                        </label>
                    </div>
                    <div className="closing-line-header-actions">
                        <RiFileExcel2Line className="excel-icon closing-line-excel" onClick={exportToExcel} title="Export to Excel" />
                        {isLoading && rows.length > 0 && (
                            <FaSyncAlt className="closing-line-inline-loader" aria-label="Refreshing" />
                        )}
                        <Link to="/analytics" className="closing-line-back">All players</Link>
                    </div>
                </div>

                <div className="closing-line-stats">
                    <div className="closing-line-stat">
                        <span className="closing-line-stat-label">Average</span>
                        <span className={`closing-line-stat-value ${signedClassName(averageValue)}`}>{averageLabel}</span>
                    </div>
                    <div className="closing-line-stat">
                        <span className="closing-line-stat-label">Win/Loss</span>
                        <span className={`closing-line-stat-value ${signedClassName(winLoss)}`}>{formatWinLoss(winLoss)}</span>
                    </div>
                </div>

                <div className="closing-line-table-wrap table-responsive">
                    {!showSkeleton && rows.length === 0 ? (
                        <div className="closing-line-empty">
                            {lineType === "price"
                                ? "No moneyline bets in the last 14 days."
                                : "No spread/total bets in the last 14 days."}
                        </div>
                    ) : (
                        <table className="closing-line-table">
                            <thead>
                                <tr>
                                    <th>Ticket</th>
                                    <th>Accepted (PST)</th>
                                    <th>Description</th>
                                    <th>Original Bet</th>
                                    <th>Final Bet</th>
                                    <th>Closing Line</th>
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showSkeleton ? (
                                    <ClosingLineTableSkeleton />
                                ) : (
                                    rows.map((row, index) => {
                                        const accepted = toClosingLineBetDate(row);
                                        const description = row.taken && row.description
                                            && !row.description.includes(row.taken)
                                            && !row.originalBet
                                            ? `${row.description} ${row.taken}`
                                            : (row.description || row.taken || "—");
                                        return (
                                            <tr key={row.betId || row.ticket || `${row.description}-${index}`}>
                                                <td>{row.ticket || "—"}</td>
                                                <td>{accepted ? formatAdminFiguresDateTime(accepted) : "—"}</td>
                                                <td className="col-customer closing-line-description">{description}</td>
                                                <td>{originalBetLabel(row)}</td>
                                                <td>{finalBetLabel(row)}</td>
                                                <td className={signedClassName(row.value)}>
                                                    {closingLineDeltaLabel(row.value, row.kind, row.valueDisplay)
                                                        || closingLineCombinedLabel(row)
                                                        || "—"}
                                                </td>
                                                <td className={signedClassName(row.winLoss)}>{formatWinLoss(row.winLoss)}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClosingLineDetail;
